import SWorker from "simple-web-worker";
import { IVueI18n } from 'vue-i18n'

import { ApexChartSeries, ApexChartSeriesItem } from '../types/ApexChartSeries.types'
import { FromToTicksReturnType } from './fromToTicks'

type PermittedTimeFieldKeys = 'time' | 'date'
type PermittedTimeFields = Record<PermittedTimeFieldKeys, string>
type SeriesInfos = Array<SeriesInfo>
type SeriesInfo = Partial<{ type: string, name: string }>
type MappedTimeTicks = Record<string, number | null>

export function seriesByMetricInList<
  U extends Partial<Record<keyof Omit<U, PermittedTimeFieldKeys>, number | null>> & Partial<PermittedTimeFields>,
  T extends keyof Omit<U, PermittedTimeFieldKeys>
>(
  i18n: IVueI18n,
  list: Array<U>,
  metrics: Array<T>,
  fromToTicks: FromToTicksReturnType,
  timeField: PermittedTimeFieldKeys = 'time'
) {

  function getMetricStatSeries(metric:T, seriesInfo: SeriesInfo | undefined, missNullable?:boolean):Promise<ApexChartSeriesItem> {
    return getStat(metric, missNullable)
      .then((statistics: ApexChartSeriesItem['data'] | undefined) => {
        return {
          name: i18n.t(`metric_${ metric.toString() }_title`).toString(),
          data: statistics ?? [],
          ...seriesInfo ? seriesInfo : []
        }
      })
  }

  function getStat(metric:T, missNullable?: boolean) {
    return SWorker.run((fromToTicks:FromToTicksReturnType, metric:T, list: Array<U>, timeField:PermittedTimeFieldKeys = 'time', missNullable?:boolean) => {
      const { timeTicks, mapTimeData } = fromToTicks
      const ticks: MappedTimeTicks = { ...mapTimeData }

      timeTicks.forEach((tick: string) => {
        const record = list.find(r => r[timeField] === tick)

        if (record && record[timeField]) {
          ticks[record[timeField]!] = record[metric] ?? (missNullable ? null : 0)
        } else {
          ticks[tick] = missNullable ? null : 0
        }
      })

      return Object.values(ticks)

    }, [ fromToTicks, metric, list, timeField, missNullable ])
      .then((res: Array<number | null>) => res)
      .catch((error:unknown) => [])
  }

  function calcSeries(seriesInfo?: SeriesInfos, missNullable?: boolean) {
    const metricsRes = metrics.map((m, i) => getMetricStatSeries(m, (seriesInfo && seriesInfo[i]) ?? undefined, missNullable))

    return Promise.allSettled(metricsRes)
      .then((res) => {
        return res.reduce((acc: ApexChartSeries, promise) => {
          if (promise.status === 'fulfilled') {
            acc.push(promise.value)
          }

          return acc
        }, [])
      })
  }

  return {
    calcSeries,
    getStat,
  }
}
